import React, { useMemo } from 'react';
import { Box, Divider, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Breadcrumb } from '../../types/Folder';
import FolderBreadcrumbs from './FolderBreadcrumbs';
import FolderViewKebabMenu from './FolderViewKebabMenu';
import useStyles from './FolderViewHeader.styles';

interface FolderViewHeaderProps {
  name: string;
  onBack: () => void;
  breadcrumbs?: Breadcrumb[];
  onNavigate?: (id: string) => void;
  folderId?: string;
  onAddSubfolder?: () => void;
  onDelete?: () => void;
  onRename?: () => void;
  belowHeaderContent?: React.ReactNode;
  /**
   * Marker prop read by ContentLayout (via `element.props.fullWidth`) to span
   * this element across the full width (md=12) instead of a single content
   * column. It is intentionally never read inside this component.
   * @see ContentLayout `isFullWidth`
   */
  // eslint-disable-next-line react/no-unused-prop-types
  fullWidth?: boolean;
}

const FolderViewHeader: React.FC<FolderViewHeaderProps> = ({
  name,
  onBack,
  breadcrumbs,
  onNavigate,
  folderId,
  onAddSubfolder,
  onDelete,
  onRename,
  belowHeaderContent,
}) => {
  const classes = useStyles();

  const effectiveBreadcrumbs = useMemo<Breadcrumb[]>(
    () =>
      breadcrumbs && breadcrumbs.length > 0
        ? breadcrumbs
        : [{ id: folderId ?? '', name }],
    [breadcrumbs, folderId, name]
  );

  const showKebab = Boolean(folderId && onAddSubfolder && onDelete);

  return (
    <>
      <Box className={classes.container}>
        <Box className={classes.leftSection}>
          <IconButton onClick={onBack} size="small" sx={{ flexShrink: 0 }}>
            <ArrowBackIcon />
          </IconButton>
          <FolderBreadcrumbs
            breadcrumbs={effectiveBreadcrumbs}
            onNavigate={onNavigate ?? (() => {})}
          />
        </Box>
        {showKebab && (
          <FolderViewKebabMenu onDelete={onDelete!} onRename={onRename} />
        )}
      </Box>
      {belowHeaderContent && <Box mb={3}>{belowHeaderContent}</Box>}
      <Divider sx={{ mb: 2 }} />
    </>
  );
};

export default React.memo(FolderViewHeader);
